import { LockFill } from '@gravity-ui/icons';
import { AgreeToTermsPolicy, type SignInIdentifier } from '@logto/schemas';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { SmartInputField } from '@/components/InputFields';
import CaptchaBox from '@/containers/CaptchaBox';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import useFocusFirstInvalidField from '@/hooks/use-focus-first-invalid-field';
import usePrefilledIdentifier from '@/hooks/use-prefilled-identifier';
import { useUsernamePolicyDescription } from '@/hooks/use-sie';
import useSingleSignOnWatch from '@/hooks/use-single-sign-on-watch';
import useTerms from '@/hooks/use-terms';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import type { IdentifierInputValue } from '@/shared/components/InputFields/SmartInputField';
import { isUsernamePolicyViolation } from '@/shared/utils/validate-username';
import { getGeneralIdentifierErrorMessage, validateIdentifierField } from '@/utils/form';

import useOnSubmit from './use-on-submit';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly signUpMethods: SignInIdentifier[];
};

type FormState = {
  identifier: IdentifierInputValue;
};

const IdentifierRegisterForm = ({ className, autoFocus, signUpMethods }: Props) => {
  const { t } = useTranslation();
  const { termsValidation, agreeToTermsPolicy } = useTerms();

  const { errorMessage, clearErrorMessage, onSubmit } = useOnSubmit();

  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const { experienceSettings } = useContext(PageContext);
  const prefilledIdentifier = usePrefilledIdentifier({ enabledIdentifiers: signUpMethods });
  const usernamePolicyDescription = useUsernamePolicyDescription();

  const {
    watch,
    handleSubmit,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
    control,
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    defaultValues: { identifier: prefilledIdentifier },
  });

  const formRef = useFocusFirstInvalidField(isSubmitting);

  // The terms modal awaits inside the submit, so without this the button spins behind it.
  const [isConfirmingTerms, setIsConfirmingTerms] = useState(false);

  const { showSingleSignOnForm, navigateToSingleSignOn } = useSingleSignOnWatch(
    watch('identifier')
  );

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage();

      void handleSubmit(async ({ identifier: { type, value } }) => {
        if (!type) {
          return;
        }

        setIdentifierInputValue({ type, value });

        if (agreeToTermsPolicy && agreeToTermsPolicy !== AgreeToTermsPolicy.Automatic) {
          setIsConfirmingTerms(true);
          const hasAgreed = await termsValidation();
          setIsConfirmingTerms(false);

          if (!hasAgreed) {
            return;
          }
        }

        if (showSingleSignOnForm) {
          await navigateToSingleSignOn();
          return;
        }

        await onSubmit(type, value);
      })(event);
    },
    [
      agreeToTermsPolicy,
      clearErrorMessage,
      handleSubmit,
      navigateToSingleSignOn,
      onSubmit,
      setIdentifierInputValue,
      showSingleSignOnForm,
      termsValidation,
    ]
  );

  return (
    <form
      ref={formRef}
      className={classNames('flex flex-col', className)}
      onSubmit={onSubmitHandler}
    >
      <div className="flex flex-col gap-4">
        <Controller
          control={control}
          name="identifier"
          rules={{
            validate: ({ type, value }) => {
              if (!type || !value) {
                return getGeneralIdentifierErrorMessage(signUpMethods, 'required');
              }

              const errorMessage = validateIdentifierField(
                type,
                value,
                experienceSettings?.usernamePolicy
              );

              if (errorMessage) {
                // The smart input shows no upfront policy description, so a policy violation
                // surfaces the full requirements sentence; hard-floor violations stay specific.
                if (usernamePolicyDescription && isUsernamePolicyViolation(errorMessage)) {
                  return usernamePolicyDescription;
                }

                return typeof errorMessage === 'string'
                  ? t(`error.${errorMessage}`)
                  : t(`error.${errorMessage.code}`, errorMessage.data ?? {});
              }

              return true;
            },
            onChange: () => {
              if (errors.identifier) {
                clearErrors('identifier');
              }
            },
          }}
          render={({ field, formState: { defaultValues } }) => (
            <SmartInputField
              autoFocus={autoFocus}
              {...field}
              defaultValue={defaultValues?.identifier?.value}
              isDanger={!!errors.identifier || !!errorMessage}
              errorMessage={errors.identifier?.message}
              enabledTypes={signUpMethods}
            />
          )}
        />
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        {showSingleSignOnForm && (
          <p className="text-sm text-muted">{t('description.single_sign_on_enabled')}</p>
        )}
        {/*
         * Hidden with CSS rather than unmounted: unmounting re-renders the form, which re-runs
         * autofill, which pins the SSO form open for an SSO-enabled autofilled value.
         */}
        <TermsAndPrivacyCheckbox
          className={classNames(agreeToTermsPolicy === AgreeToTermsPolicy.Automatic && 'hidden')}
        />
        <CaptchaBox />
      </div>
      <Button
        name="submit"
        className="mt-6"
        title={showSingleSignOnForm ? 'action.single_sign_on' : 'action.create_account'}
        icon={showSingleSignOnForm ? <LockFill /> : undefined}
        htmlType="submit"
        isLoading={isSubmitting && !isConfirmingTerms}
      />
      <input hidden type="submit" />
    </form>
  );
};

export default IdentifierRegisterForm;
